/**
 * Maps a `GroupCategory` to the lucide icon name that represents it.
 *
 * Both web (`apps/web/src/utils/groupCategoryIcons.tsx`) and mobile
 * (`apps/mobile/src/utils/groupCategoryIcons.ts`) re-export the same
 * mapping resolved against their own platform's lucide package
 * (`lucide-react` vs `lucide-react-native`). Keeping the mapping
 * itself here means a category gets the same icon on both platforms,
 * and adding/changing a category's icon is a single-file change.
 */
import type { GroupCategory } from '../types';

/** Names match lucide's PascalCase component exports. */
export type LucideIconName =
  | 'Briefcase'
  | 'CircleDot'
  | 'Heart'
  | 'Home'
  | 'House'
  | 'PartyPopper'
  | 'Plane'
  | 'Users';

export const GROUP_CATEGORY_ICON_NAMES: Record<GroupCategory, LucideIconName> =
  {
    friends: 'Users',
    family: 'Home',
    couple: 'Heart',
    roommates: 'House',
    trip: 'Plane',
    events: 'PartyPopper',
    work: 'Briefcase',
    other: 'CircleDot',
  };

export const DEFAULT_GROUP_CATEGORY_ICON_NAME: LucideIconName = 'Users';

export function getGroupCategoryIconName(
  category: GroupCategory | string | undefined
): LucideIconName {
  if (!category) return DEFAULT_GROUP_CATEGORY_ICON_NAME;
  return (
    (GROUP_CATEGORY_ICON_NAMES as Record<string, LucideIconName>)[category] ??
    DEFAULT_GROUP_CATEGORY_ICON_NAME
  );
}
